import React, { useRef, useCallback, useEffect } from "react";
import { useDAW } from "./DAWProvider";
import { PIXELS_PER_BEAT } from "./TimelineRuler";

const LANE_HEIGHT = 60;
const POINT_RADIUS = 5;
const COLOR = "#fdd835";
const COLOR_FILL = "rgba(253, 216, 53, 0.15)";
const DRAG_THRESHOLD = 4;
const SUBDIVISION = 4; // snap to 16th notes
const SNAP_WIDTH = PIXELS_PER_BEAT / SUBDIVISION;

export default function AutomationLane({ trackId, automation, totalWidth, bpm }) {
  const { setTrackAutomation } = useDAW();
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  // --- coordinate helpers ---
  const beatToX = useCallback((beat) => beat * PIXELS_PER_BEAT, []);
  const xToBeat = useCallback((x) => x / PIXELS_PER_BEAT, []);
  const valueToY = useCallback((val) => LANE_HEIGHT - (val / 100) * LANE_HEIGHT, []);
  const yToValue = useCallback((y) => Math.max(0, Math.min(100, ((LANE_HEIGHT - y) / LANE_HEIGHT) * 100)), []);

  const snapBeat = useCallback((beat) => {
    const step = 1 / SUBDIVISION;
    return Math.round(beat / step) * step;
  }, []);

  const hitTest = useCallback((x, y) => {
    for (let i = 0; i < automation.length; i++) {
      const px = beatToX(automation[i].beat);
      const py = valueToY(automation[i].value);
      const dx = x - px;
      const dy = y - py;
      if (dx * dx + dy * dy <= (POINT_RADIUS + 3) * (POINT_RADIUS + 3)) {
        return i;
      }
    }
    return -1;
  }, [automation, beatToX, valueToY]);

  // --- drawing ---
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    canvas.width = totalWidth * dpr;
    canvas.height = LANE_HEIGHT * dpr;
    canvas.style.width = `${totalWidth}px`;
    canvas.style.height = `${LANE_HEIGHT}px`;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, totalWidth, LANE_HEIGHT);

    // Draw beat grid lines
    const totalBeats = totalWidth / PIXELS_PER_BEAT;
    for (let b = 0; b <= totalBeats; b++) {
      const x = b * PIXELS_PER_BEAT;
      ctx.strokeStyle = b % 4 === 0 ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.05)";
      ctx.lineWidth = b % 4 === 0 ? 1 : 0.5;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, LANE_HEIGHT);
      ctx.stroke();
    }

    if (automation.length === 0) {
      // Draw hint text
      ctx.fillStyle = "rgba(253, 216, 53, 0.3)";
      ctx.font = "11px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Click to add automation points", totalWidth / 2, LANE_HEIGHT / 2 + 4);
      return;
    }

    // Build point coordinates
    const pts = automation.map((p) => ({
      x: beatToX(p.beat),
      y: valueToY(p.value),
    }));

    // Filled area under the curve
    ctx.beginPath();
    ctx.moveTo(pts[0].x, LANE_HEIGHT);
    pts.forEach((p) => ctx.lineTo(p.x, p.y));
    ctx.lineTo(pts[pts.length - 1].x, LANE_HEIGHT);
    ctx.closePath();
    ctx.fillStyle = COLOR_FILL;
    ctx.fill();

    // Stroke line connecting points
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(pts[i].x, pts[i].y);
    }
    ctx.strokeStyle = COLOR;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Draw handles
    pts.forEach((p, i) => {
      const isDragging = dragRef.current?.mode === "drag" && dragRef.current.pointIndex === i;
      ctx.beginPath();
      ctx.arc(p.x, p.y, isDragging ? POINT_RADIUS + 1 : POINT_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = COLOR;
      ctx.fill();
      ctx.strokeStyle = "#1a1a2e";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });
  }, [automation, totalWidth, beatToX, valueToY]);

  useEffect(() => { draw(); }, [draw]);

  // --- pointer events ---
  const getCanvasPos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (e.button === 2) {
      // Right-click: delete point
      e.preventDefault();
      const { x, y } = getCanvasPos(e);
      const idx = hitTest(x, y);
      if (idx >= 0) {
        const next = automation.filter((_, i) => i !== idx);
        setTrackAutomation(trackId, next);
      }
      return;
    }

    const { x, y } = getCanvasPos(e);
    const idx = hitTest(x, y);

    dragRef.current = {
      mode: idx >= 0 ? "pending-drag" : "pending-add",
      pointIndex: idx,
      startX: e.clientX,
      startY: e.clientY,
      canvasX: x,
      canvasY: y,
    };

    canvasRef.current.setPointerCapture(e.pointerId);
  }, [automation, trackId, getCanvasPos, hitTest, setTrackAutomation]);

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { mode, startX, startY, pointIndex } = dragRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    if (mode === "pending-drag" || mode === "pending-add") {
      if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
      if (mode === "pending-drag") {
        dragRef.current.mode = "drag";
      } else {
        // Start by adding a new point, then drag it
        const beat = snapBeat(xToBeat(dragRef.current.canvasX));
        const value = Math.round(yToValue(dragRef.current.canvasY));
        const newPoints = [...automation, { beat, value }].sort((a, b) => a.beat - b.beat);
        const newIdx = newPoints.findIndex((p) => p.beat === beat && p.value === value);
        setTrackAutomation(trackId, newPoints);
        dragRef.current.mode = "drag";
        dragRef.current.pointIndex = newIdx;
        return;
      }
    }

    if (mode === "drag" && pointIndex >= 0 && pointIndex < automation.length) {
      const { x, y } = getCanvasPos(e);
      const beat = snapBeat(Math.max(0, xToBeat(x)));
      const value = Math.round(yToValue(y));
      const next = automation.map((p, i) =>
        i === pointIndex ? { beat, value } : p
      );
      setTrackAutomation(trackId, next);
    }
  }, [automation, trackId, getCanvasPos, snapBeat, xToBeat, yToValue, setTrackAutomation]);

  const handlePointerUp = useCallback((e) => {
    if (!dragRef.current) return;
    const { mode, canvasX, canvasY } = dragRef.current;

    // If it was a click (no drag), add a point
    if (mode === "pending-add") {
      const beat = snapBeat(xToBeat(canvasX));
      const value = Math.round(yToValue(canvasY));
      const next = [...automation, { beat, value }].sort((a, b) => a.beat - b.beat);
      setTrackAutomation(trackId, next);
    }

    canvasRef.current.releasePointerCapture(e.pointerId);
    dragRef.current = null;
  }, [automation, trackId, snapBeat, xToBeat, yToValue, setTrackAutomation]);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="daw-automation-canvas"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onContextMenu={handleContextMenu}
    />
  );
}
